import React, { useRef } from 'react'
import {
  Form,
  Input,
  Card,
  Typography,
  Flex,
  Button,
  Select,
  Table,
  Empty,
  Tag,
} from 'antd'
import { PlusOutlined, DeleteOutlined, HolderOutlined } from '@ant-design/icons'
import type { FlowStep, FlowStepInput } from '@/data'
import { availableFormInputs, requiredFormFieldIds } from './config'

const { Text } = Typography

interface FormStepEditorProps {
  formSteps: FlowStep[]
  onChange: (steps: FlowStep[]) => void
  onDeleteStep?: (stepId: number) => void
}

export const FormStepEditor: React.FC<FormStepEditorProps> = ({
  formSteps,
  onChange,
  onDeleteStep,
}) => {
  const dragItem = useRef<{ stepId: number; index: number } | null>(null)
  const dragOverItem = useRef<{ stepId: number; index: number } | null>(null)

  const isRequiredField = (fieldId: string): boolean => {
    return requiredFormFieldIds.includes(
      fieldId as (typeof requiredFormFieldIds)[number],
    )
  }

  const getUsedInputIds = (): Set<string> => {
    const usedIds = new Set<string>()
    formSteps.forEach((step) => {
      step.inputs.forEach((input) => {
        usedIds.add(input.id)
      })
    })
    return usedIds
  }

  const getUnusedInputs = (): FlowStepInput[] => {
    const usedIds = getUsedInputIds()
    return availableFormInputs.filter((input) => !usedIds.has(input.id))
  }

  const addStep = () => {
    const newStep: FlowStep = {
      ordinal: formSteps.length + 1,
      headline: 'New Form Step',
      sub_headline: '',
      cta_button: 'Continue',
      inputs: [],
    }
    onChange([...formSteps, newStep])
  }

  const removeStep = (stepIndex: number) => {
    if (formSteps.length <= 1) return

    const stepToRemove = formSteps[stepIndex]

    const requiredFieldsToMove = stepToRemove.inputs.filter((input) =>
      isRequiredField(input.id),
    )

    const targetStepIndex = stepIndex > 0 ? stepIndex - 1 : 1

    const updatedSteps = formSteps.map((step, idx) => {
      if (idx === targetStepIndex) {
        return {
          ...step,
          inputs: [...step.inputs, ...requiredFieldsToMove],
        }
      }
      return step
    })

    onChange(updatedSteps.filter((_, idx) => idx !== stepIndex))
    if (stepToRemove.id != null) onDeleteStep?.(stepToRemove.id)
  }

  const updateStepField = (
    stepId: number,
    field: 'headline' | 'sub_headline' | 'cta_button',
    value: string,
  ) => {
    onChange(
      formSteps.map((step, idx) =>
        idx === stepId ? { ...step, [field]: value } : step,
      ),
    )
  }

  const addInputToStep = (stepId: number, inputId: string) => {
    const input = availableFormInputs.find((i) => i.id === inputId)
    if (!input) return

    onChange(
      formSteps.map((step, idx) =>
        idx === stepId ? { ...step, inputs: [...step.inputs, input] } : step,
      ),
    )
  }

  const removeInputFromStep = (stepId: number, inputId: string) => {
    if (isRequiredField(inputId)) return

    onChange(
      formSteps.map((step, idx) =>
        idx === stepId
          ? { ...step, inputs: step.inputs.filter((i) => i.id !== inputId) }
          : step,
      ),
    )
  }

  const moveInputToStep = (
    fromStepId: number,
    inputId: string,
    toStepId: number,
  ) => {
    const input = formSteps[fromStepId]?.inputs.find((i) => i.id === inputId)
    if (!input || fromStepId === toStepId) return

    onChange(
      formSteps.map((step, idx) => {
        if (idx === fromStepId) {
          return {
            ...step,
            inputs: step.inputs.filter((i) => i.id !== inputId),
          }
        }
        if (idx === toStepId) {
          return { ...step, inputs: [...step.inputs, input] }
        }
        return step
      }),
    )
  }

  const handleDragStart = (stepId: number, index: number) => {
    dragItem.current = { stepId, index }
  }

  const handleDragEnter = (stepId: number, index: number) => {
    dragOverItem.current = { stepId, index }
  }

  const handleDragEnd = (stepId: number) => {
    if (!dragItem.current || !dragOverItem.current) return

    if (dragItem.current.stepId !== dragOverItem.current.stepId) {
      const input =
        formSteps[dragItem.current.stepId]?.inputs[dragItem.current.index]
      if (input) {
        onChange(
          formSteps.map((step, idx) => {
            if (idx === dragItem.current!.stepId) {
              return {
                ...step,
                inputs: step.inputs.filter(
                  (_, i) => i !== dragItem.current!.index,
                ),
              }
            }
            if (idx === dragOverItem.current!.stepId) {
              const newInputs = [...step.inputs]
              newInputs.splice(dragOverItem.current!.index, 0, input)
              return { ...step, inputs: newInputs }
            }
            return step
          }),
        )
      }
    } else {
      onChange(
        formSteps.map((step, idx) => {
          if (idx !== stepId) return step
          const newInputs = [...step.inputs]
          const draggedItem = newInputs.splice(dragItem.current!.index, 1)[0]
          newInputs.splice(dragOverItem.current!.index, 0, draggedItem)
          return { ...step, inputs: newInputs }
        }),
      )
    }

    dragItem.current = null
    dragOverItem.current = null
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
  }

  const getTableColumns = (stepId: number) => [
    {
      title: '',
      width: 40,
      render: (_: unknown, __: FlowStepInput, index: number) => (
        <HolderOutlined
          style={{ cursor: 'grab', color: '#999' }}
          draggable
          onDragStart={() => handleDragStart(stepId, index)}
          onDragEnter={() => handleDragEnter(stepId, index)}
          onDragEnd={() => handleDragEnd(stepId)}
          onDragOver={handleDragOver}
        />
      ),
    },
    {
      title: 'Field',
      dataIndex: 'label',
      render: (label: string, record: FlowStepInput) => (
        <span>
          {label}
          {isRequiredField(record.id) && <Text type="danger"> *</Text>}
        </span>
      ),
    },
    {
      title: 'Move To',
      width: 120,
      render: (_: unknown, record: FlowStepInput) =>
        formSteps.length > 1 ? (
          <Select
            size="small"
            style={{ width: 100 }}
            placeholder="Step..."
            value={undefined}
            onChange={(toStepId: number) =>
              moveInputToStep(stepId, record.id, toStepId)
            }
            options={formSteps
              .map((_, idx) => ({
                value: idx,
                label: `Step ${idx + 1}`,
              }))
              .filter((opt) => opt.value !== stepId)}
          />
        ) : null,
    },
    {
      title: '',
      width: 40,
      render: (_: unknown, record: FlowStepInput) =>
        !isRequiredField(record.id) ? (
          <Button
            type="text"
            size="small"
            danger
            icon={<DeleteOutlined />}
            onClick={() => removeInputFromStep(stepId, record.id)}
          />
        ) : null,
    },
  ]

  return (
    <>
      {formSteps.map((step, stepIndex) => (
        <Card
          key={step.id ?? stepIndex}
          size="small"
          title={
            <Flex gap="small" align="center">
              <Tag color="green">Form Step {stepIndex + 1}</Tag>
            </Flex>
          }
          extra={
            formSteps.length > 1 && (
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
                onClick={() => removeStep(stepIndex)}
                size="small"
              >
                Remove
              </Button>
            )
          }
          style={{ backgroundColor: '#fafafa' }}
        >
          <Form layout="vertical" size="small">
            <Form.Item label="Headline">
              <Input
                value={step.headline}
                onChange={(e) =>
                  updateStepField(stepIndex, 'headline', e.target.value)
                }
                placeholder="Enter headline"
              />
            </Form.Item>

            <Form.Item label="Sub Headline">
              <Input
                value={step.sub_headline}
                onChange={(e) =>
                  updateStepField(stepIndex, 'sub_headline', e.target.value)
                }
                placeholder="Enter sub headline"
              />
            </Form.Item>

            <Form.Item label="Form Fields">
              {step.inputs.length === 0 ? (
                <Empty
                  description="No form fields added"
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  style={{ margin: '8px 0' }}
                />
              ) : (
                <Table
                  dataSource={step.inputs}
                  columns={getTableColumns(stepIndex)}
                  rowKey="id"
                  pagination={false}
                  size="small"
                  showHeader={false}
                  onRow={(_, index) => ({
                    draggable: true,
                    onDragStart: () => handleDragStart(stepIndex, index!),
                    onDragEnter: () => handleDragEnter(stepIndex, index!),
                    onDragEnd: () => handleDragEnd(stepIndex),
                    onDragOver: handleDragOver,
                    style: { cursor: 'grab' },
                  })}
                />
              )}

              {getUnusedInputs().length > 0 && (
                <Select
                  placeholder="+ Add field"
                  style={{ width: 200, marginTop: 8 }}
                  value={undefined}
                  onChange={(inputId: string) =>
                    addInputToStep(stepIndex, inputId)
                  }
                  options={getUnusedInputs().map((input) => ({
                    value: input.id,
                    label: input.label,
                  }))}
                />
              )}
            </Form.Item>

            <Form.Item label="CTA Button Text">
              <Input
                value={step.cta_button}
                onChange={(e) =>
                  updateStepField(stepIndex, 'cta_button', e.target.value)
                }
                placeholder="Enter button text"
                style={{ maxWidth: 250 }}
              />
            </Form.Item>
          </Form>
        </Card>
      ))}

      <Button
        type="dashed"
        icon={<PlusOutlined />}
        onClick={addStep}
        style={{ width: '100%' }}
      >
        Add Form Step
      </Button>
    </>
  )
}
